import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fileUrl, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Camera, CheckCircle, ArrowLeft, MapPin, ChatText, Play, X,
  ImageSquare, Warning, Sparkle,
} from "@phosphor-icons/react";

/**
 * SimpleJobView — the "grandma phone" of worker screens.
 *
 * Design brief:
 *   Address at top → giant Camera button → optional note → giant Done button.
 *   Nothing else. No tabs, no clock-in modal, no chargeback checkbox, no
 *   materials list. If a tech needs those, they toggle Simple Mode OFF in the
 *   header and get the full JobDetail view — same underlying data.
 *
 * Reuses the existing POST /api/jobs/{id}/photo endpoint and the bulk
 * quick-close pipeline (needs_docs=true, photos owed later) for completion.
 */
export default function SimpleJobView({ job, onReload }) {
  const nav = useNavigate();
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  // Only preload notes the tech previously saved — never the customer's
  // complaint description, which would confuse "what did I do" with "what
  // was reported".
  const initialNote = job.tech_notes || "";
  const [note, setNote] = useState(initialNote);
  const [savedNote, setSavedNote] = useState(initialNote);
  const [savingNote, setSavingNote] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [showPhoto, setShowPhoto] = useState(null);
  const inputRef = useRef(null);
  const cameraRef = useRef(null);

  const isDone = job.status === "completed" || job.status === "canceled";
  const noteDirty = note !== savedNote;
  const allPhotos = [...(job.before_photos || []), ...(job.after_photos || [])];
  // Unclaimed jobs (assigned_to=null) 403 on every write — hide the write UI
  // and steer the tech to claim through the full view.
  const isMine = !!job.assigned_to || !!job.helper_id;

  const doUpload = async (files) => {
    if (!files || files.length === 0) return;
    const arr = Array.from(files);
    setUploading(true);
    setProgress({ done: 0, total: arr.length });
    let ok = 0, failed = 0;
    // Serial upload keeps the UI honest about progress and avoids saturating
    // spotty jobsite cellular.
    for (const f of arr) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("kind", "after");
        await api.post(`/jobs/${job.id}/photo`, fd,
          { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 });
        ok += 1;
      } catch (e) {
        failed += 1;
      }
      setProgress(p => ({ ...p, done: p.done + 1 }));
    }
    setUploading(false);
    if (failed) toast.error(`${failed} photo${failed === 1 ? "" : "s"} failed to upload`);
    if (ok)     toast.success(`${ok} photo${ok === 1 ? "" : "s"} added`);
    await onReload();
  };

  const saveNote = async () => {
    if (savingNote) return true;
    setSavingNote(true);
    try {
      const r = await api.post(`/jobs/${job.id}/tech-note`, { tech_notes: note });
      setSavedNote(r.data?.tech_notes ?? note);
      toast.success("Note saved");
      return true;
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Couldn't save note"));
      return false;
    } finally {
      setSavingNote(false);
    }
  };

  const markDone = async () => {
    if (completing) return;
    setCompleting(true);
    try {
      // Send the note in the same quick-close call so it can never be lost
      // between two round trips. Backend accepts tech_notes on QuickCloseIn.
      await api.post(`/jobs/${job.id}/quick-close`, {
        status: "completed",
        reason: "",
        tech_notes: note,
      });
      toast.success("Marked done ✓");
      nav("/worker");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Couldn't mark done"));
      setCompleting(false);
    }
  };

  const mapHref = job.property_address
    ? `https://maps.apple.com/?q=${encodeURIComponent(job.property_address)}`
    : null;

  return (
    <div className="min-h-screen bg-[#f4f4f5]" data-testid="simple-job-view">
      {/* Header — big back button, no toolbar chrome */}
      <div className="bg-white border-b-2 border-slate-200">
        <div className="max-w-md mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => nav("/worker")} className="h-11 w-11 border-2 border-[#0f172a] flex items-center justify-center hover:bg-slate-50" data-testid="simple-back-btn">
            <ArrowLeft weight="bold" size={20}/>
          </button>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">
              {job.wo_number ? `WO #${job.wo_number}` : "Work Order"}
              {isDone && <span className="ml-2 px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded">Done</span>}
            </div>
            <div className="font-display font-black text-lg leading-tight truncate">
              {job.title || "Job"}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-md mx-auto p-4 space-y-5">
        {/* Address card */}
        {job.property_address && (
          <a
            href={mapHref}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="simple-address-map-link"
            className="flex items-center gap-3 p-4 bg-white border-2 border-[#0f172a] shadow-[3px_3px_0_#0f172a] hover:bg-slate-50 active:translate-x-[1px] active:translate-y-[1px] active:shadow-[2px_2px_0_#0f172a] transition-transform">
            <MapPin weight="fill" size={28} className="text-[#00C4CC] flex-shrink-0"/>
            <div className="flex-1 min-w-0">
              <div className="font-mono text-base font-bold text-slate-900 truncate">{job.property_address}</div>
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Tap to open maps</div>
            </div>
          </a>
        )}

        {/* Unclaimed banner — writes will 403, so steer to full view */}
        {!isMine && !isDone && (
          <div className="p-3 border-2 border-amber-500 bg-amber-50 text-xs" data-testid="simple-unclaimed-banner">
            <div className="font-black uppercase tracking-widest text-amber-800 mb-1">
              <Warning weight="fill" size={14} className="inline mr-1"/> Not assigned to you yet
            </div>
            <div className="text-amber-900">
              This WO is in the pool. Turn off Simple Mode (toggle in the header) and hit "Claim" first, then come back.
            </div>
          </div>
        )}

        {/* Photos preview + big upload buttons */}
        <div className="bg-white border-2 border-[#0f172a] p-4 shadow-[3px_3px_0_#0f172a]">
          <div className="flex items-center justify-between mb-3">
            <div className="font-display font-black text-sm uppercase tracking-wide">
              Photos <span className="text-slate-400 font-mono text-xs">({allPhotos.length})</span>
            </div>
            {uploading && (
              <div className="text-[11px] font-black uppercase tracking-widest text-[#00C4CC]">
                Uploading {progress.done}/{progress.total}…
              </div>
            )}
          </div>

          {/* Thumbnails — max 8 shown, tap opens lightbox */}
          {allPhotos.length > 0 && (
            <div className="grid grid-cols-4 gap-2 mb-3">
              {allPhotos.slice(0, 8).map((p, i) => (
                <button key={p.file_id || i}
                  onClick={() => setShowPhoto(p)}
                  className="aspect-square border-2 border-slate-200 overflow-hidden bg-slate-100 hover:border-slate-900 transition-colors"
                  data-testid={`simple-photo-thumb-${i}`}>
                  {p.media_type === "video" ? (
                    <div className="w-full h-full relative bg-slate-800">
                      <video src={fileUrl(p.path)} className="w-full h-full object-cover" muted playsInline preload="metadata"/>
                      <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                        <div className="h-6 w-6 bg-white rounded-full flex items-center justify-center">
                          <Play weight="fill" size={12} className="text-[#0f172a] ml-0.5"/>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <img src={fileUrl(p.path)} alt="" className="w-full h-full object-cover"/>
                  )}
                </button>
              ))}
              {allPhotos.length > 8 && (
                <div className="aspect-square border-2 border-dashed border-slate-300 flex items-center justify-center text-xs font-black text-slate-500">
                  +{allPhotos.length - 8}
                </div>
              )}
            </div>
          )}

          {/* Big camera + library buttons */}
          <div className="grid grid-cols-2 gap-2">
            <input
              ref={cameraRef}
              type="file"
              accept="image/*,video/*"
              capture="environment"
              multiple
              className="hidden"
              onChange={e => { doUpload(e.target.files); e.target.value = ""; }}
              data-testid="simple-camera-input"/>
            <button
              onClick={() => cameraRef.current?.click()}
              disabled={uploading || isDone || !isMine}
              data-testid="simple-camera-btn"
              className="h-20 bg-[#00C4CC] hover:bg-cyan-500 text-[#0f172a] border-2 border-[#0f172a] shadow-[3px_3px_0_#0f172a] font-black uppercase tracking-widest text-sm flex flex-col items-center justify-center gap-1 disabled:opacity-50 active:translate-x-[1px] active:translate-y-[1px] active:shadow-[2px_2px_0_#0f172a] transition-transform">
              <Camera weight="fill" size={26}/>
              Take Photo
            </button>
            <input
              ref={inputRef}
              type="file"
              accept="image/*,video/*"
              multiple
              className="hidden"
              onChange={e => { doUpload(e.target.files); e.target.value = ""; }}
              data-testid="simple-library-input"/>
            <button
              onClick={() => inputRef.current?.click()}
              disabled={uploading || isDone || !isMine}
              data-testid="simple-library-btn"
              className="h-20 bg-white hover:bg-slate-50 text-[#0f172a] border-2 border-[#0f172a] shadow-[3px_3px_0_#0f172a] font-black uppercase tracking-widest text-sm flex flex-col items-center justify-center gap-1 disabled:opacity-50 active:translate-x-[1px] active:translate-y-[1px] active:shadow-[2px_2px_0_#0f172a] transition-transform">
              <ImageSquare weight="fill" size={26}/>
              From Library
            </button>
          </div>
        </div>

        {/* Note */}
        <div className="bg-white border-2 border-[#0f172a] p-4 shadow-[3px_3px_0_#0f172a]">
          <div className="flex items-center gap-1 mb-2">
            <ChatText weight="fill" size={16}/>
            <div className="font-display font-black text-sm uppercase tracking-wide">Note (optional)</div>
          </div>
          <textarea
            rows={3}
            value={note}
            onChange={e => setNote(e.target.value)}
            disabled={isDone}
            placeholder="What did you do? Any parts, follow-up, or gotchas?"
            data-testid="simple-note-textarea"
            className="w-full p-2 border-2 border-slate-300 text-sm resize-none focus:border-[#00C4CC] outline-none disabled:bg-slate-50"/>
          {!isDone && noteDirty && (
            <button
              onClick={saveNote}
              disabled={savingNote}
              data-testid="simple-note-save-btn"
              className="mt-2 h-9 px-3 bg-[#0f172a] text-white font-black uppercase text-[10px] tracking-widest disabled:opacity-60">
              {savingNote ? "Saving…" : "Save note"}
            </button>
          )}
        </div>

        {/* Big Done button */}
        {!isDone && isMine && (
          <button
            onClick={markDone}
            disabled={completing}
            data-testid="simple-done-btn"
            className="w-full h-20 bg-emerald-600 hover:bg-emerald-700 text-white border-2 border-[#0f172a] shadow-[3px_3px_0_#0f172a] font-black uppercase tracking-widest text-lg flex items-center justify-center gap-2 disabled:opacity-60 active:translate-x-[1px] active:translate-y-[1px] active:shadow-[2px_2px_0_#0f172a] transition-transform">
            <CheckCircle weight="fill" size={30}/>
            {completing ? "Marking done…" : "Mark this WO done"}
          </button>
        )}

        {allPhotos.length === 0 && !isDone && (
          <div className="flex items-center gap-2 p-3 border-2 border-amber-400 bg-amber-50 text-xs text-amber-900" data-testid="simple-no-photos-hint">
            <Warning weight="fill" size={16} className="flex-shrink-0"/>
            <div>Try to snap at least one "after" photo before marking done — protects you if the landlord ever pushes back.</div>
          </div>
        )}

        {/* Simple/Full mode helper */}
        <div className="text-center text-[11px] text-slate-500 py-2">
          <Sparkle weight="fill" size={10} className="inline mr-1"/>
          Simple mode is ON. Tap the toggle in the header to see all options.
        </div>
      </div>

      {/* Lightbox */}
      {showPhoto && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setShowPhoto(null)} data-testid="simple-photo-viewer">
          <button className="absolute top-4 right-4 h-11 w-11 bg-white rounded-full flex items-center justify-center" aria-label="Close">
            <X weight="bold" size={22}/>
          </button>
          {showPhoto.media_type === "video" ? (
            <video src={fileUrl(showPhoto.path)} controls autoPlay
              className="max-h-[90vh] max-w-[92vw]" onClick={e => e.stopPropagation()}/>
          ) : (
            <img src={fileUrl(showPhoto.path)} alt="" className="max-h-[90vh] max-w-[92vw] object-contain" onClick={e => e.stopPropagation()}/>
          )}
        </div>
      )}
    </div>
  );
}
